/**
 * EntityTable — canonical virtualized table for any entity type (§1E).
 *
 * Same table UX for items, work units, capacity rows, scenarios, etc.:
 * checkbox multi-select, shift-range select, sortable/resizable columns,
 * keyboard navigation, flex-height fill in list shells.
 */

import { useRef } from 'react';
import { VirtualizedTable, type VirtualizedTableColumn } from './VirtualizedTable';
import type { SortState } from './types';
import { useContainerHeight } from './useContainerHeight';

const MIN_TABLE_HEIGHT = 280;

export interface EntityTableProps<T, SortField extends string = string> {
  items: T[];
  getId: (item: T) => string;
  columns: VirtualizedTableColumn<T>[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectRange?: (fromId: string, toId: string) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  onRowClick?: (item: T) => void;
  onRowDoubleClick?: (item: T) => void;
  /** Checkbox column + bulk selection (default true). */
  selectable?: boolean;
  currentSort?: SortState<SortField>;
  onSortChange?: (field: SortField, direction?: 'asc' | 'desc') => void;
  rowHeight?: number;
  focusedId?: string | null;
  onFocusChange?: (id: string | null) => void;
  enableKeyboardNavigation?: boolean;
  className?: string;
  columnWidths?: Record<string, number>;
  onColumnWidthChange?: (columnKey: string, width: number) => void;
}

export function EntityTable<T, SortField extends string = string>({
  items,
  getId,
  columns,
  selectedIds,
  onToggle,
  onSelectRange,
  onSelectAll,
  onDeselectAll,
  onRowClick,
  onRowDoubleClick,
  selectable = true,
  currentSort,
  onSortChange,
  rowHeight = 52,
  focusedId,
  onFocusChange,
  enableKeyboardNavigation = true,
  className = '',
  columnWidths,
  onColumnWidthChange,
}: EntityTableProps<T, SortField>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const height = useContainerHeight(containerRef);

  return (
    <div ref={containerRef} className={`flex-1 min-h-0 min-w-0 h-full ${className}`} style={{ minHeight: MIN_TABLE_HEIGHT }}>
      {height > 0 && (
        <VirtualizedTable
          items={items}
          getId={getId}
          columns={columns}
          selectedIds={selectedIds}
          onToggle={onToggle}
          onSelectRange={onSelectRange}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
          onRowClick={onRowClick}
          onRowDoubleClick={onRowDoubleClick}
          selectable={selectable}
          currentSort={currentSort}
          onSortChange={onSortChange}
          rowHeight={rowHeight}
          focusedId={focusedId}
          onFocusChange={onFocusChange}
          enableKeyboardNavigation={enableKeyboardNavigation}
          height={height}
          className="h-full"
          columnWidths={columnWidths}
          onColumnWidthChange={onColumnWidthChange}
        />
      )}
    </div>
  );
}

export type { VirtualizedTableColumn };
